import { useState, type DragEvent } from "react";
import { Button, EmptyState, QueryStateBoundary } from "@/components/ui";
import { STAGES, STAGE_LABELS, type InquiryStage } from "@/api/types/inquiry";
import { useInquiriesBoard, useUpdateInquiryStage } from "@/api/hooks/useInquiries";
import { InquiryCard } from "./InquiryCard";
import { InquiryFormDialog } from "./InquiryFormDialog";
import { InquiryDetailDialog } from "./InquiryDetailDialog";
import { LostReasonDialog } from "./LostReasonDialog";
import { toastError } from "@/components/ui/Toast";
import { getErrorMessage } from "@/api/client";

export function PipelinePage() {
  const { data, isLoading, error, refetch } = useInquiriesBoard();
  const updateStage = useUpdateInquiryStage();

  const [formOpen, setFormOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingLostId, setPendingLostId] = useState<string | null>(null);

  function handleDragStart(e: DragEvent<HTMLDivElement>, id: string) {
    e.dataTransfer.setData("text/plain", id);
    e.dataTransfer.effectAllowed = "move";
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }

  function handleDrop(e: DragEvent<HTMLDivElement>, stage: InquiryStage) {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    if (!id) return;

    const currentStage = data?.columns.find((col) =>
      col.items.some((item) => item.id === id),
    )?.stage;
    if (currentStage === stage) return;

    if (stage === "closed_lost") {
      // Don't touch the cache until a reason is confirmed — the card stays
      // put in its source column if the dialog is canceled.
      setPendingLostId(id);
      return;
    }

    updateStage.mutate(
      { id, stage },
      { onError: (err) => toastError("Couldn't move inquiry", getErrorMessage(err)) },
    );
  }

  function confirmLost(reason: string) {
    if (!pendingLostId) return;
    updateStage.mutate(
      { id: pendingLostId, stage: "closed_lost", lost_reason: reason },
      {
        onSuccess: () => setPendingLostId(null),
        onError: (err) => {
          toastError("Couldn't mark inquiry lost", getErrorMessage(err));
          setPendingLostId(null);
        },
      },
    );
  }

  const totalItems = data?.columns.reduce((sum, col) => sum + col.items.length, 0) ?? 0;

  return (
    <div className="flex h-full flex-col p-6 sm:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-display text-2xl font-bold text-text-primary">Pipeline</h1>
        {totalItems > 0 && <Button onClick={() => setFormOpen(true)}>New inquiry</Button>}
      </div>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {totalItems === 0 ? (
          <EmptyState
            icon="📋"
            title="No inquiries yet"
            description="Every lead — DM, website form, or referral — lands here first."
            action={{ label: "New inquiry", onClick: () => setFormOpen(true) }}
          />
        ) : (
          <div className="flex-1 overflow-x-auto pb-4">
            <div className="flex min-h-full gap-4">
              {STAGES.map((stage) => {
                const items = data?.columns.find((col) => col.stage === stage)?.items ?? [];
                const isNew = stage === "new";
                const isLost = stage === "closed_lost";
                return (
                  <div
                    key={stage}
                    data-stage-column={stage}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, stage)}
                    className={`flex w-72 shrink-0 flex-col rounded-xl ${
                      isNew
                        ? "bg-copper-light/60 ring-1 ring-inset ring-copper/25"
                        : isLost
                          ? "bg-bg-muted/30"
                          : "bg-bg-muted/50"
                    }`}
                  >
                    <div className="flex items-center justify-between px-3 py-3">
                      <h2
                        className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.08em] ${
                          isNew ? "text-copper" : isLost ? "text-text-muted" : "text-text-secondary"
                        }`}
                      >
                        {isNew && items.length > 0 && (
                          <span
                            aria-hidden="true"
                            className="h-1.5 w-1.5 rounded-full bg-copper motion-safe:animate-pulse"
                          />
                        )}
                        {STAGE_LABELS[stage]}
                      </h2>
                      <span
                        className={`tabular rounded-full px-2 py-0.5 text-xs font-semibold ${
                          isNew && items.length > 0
                            ? "bg-copper text-white"
                            : "bg-bg-card text-text-secondary ring-1 ring-inset ring-border"
                        }`}
                      >
                        {items.length}
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col gap-2 px-2 pb-2">
                      {items.map((item) => (
                        <InquiryCard
                          key={item.id}
                          item={item}
                          onOpen={() => setSelectedId(item.id)}
                          onDragStart={(e) => handleDragStart(e, item.id)}
                        />
                      ))}
                      {items.length === 0 && (
                        <div
                          aria-hidden="true"
                          className="mx-1 mb-1 rounded-lg border border-dashed border-border-strong/60 py-6 text-center text-xs text-text-muted"
                        >
                          No inquiries
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </QueryStateBoundary>

      <InquiryFormDialog open={formOpen} onClose={() => setFormOpen(false)} />

      <InquiryDetailDialog inquiryId={selectedId} onClose={() => setSelectedId(null)} />

      <LostReasonDialog
        open={!!pendingLostId}
        onCancel={() => setPendingLostId(null)}
        onConfirm={confirmLost}
        isSubmitting={updateStage.isPending}
      />
    </div>
  );
}
